import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

/**
 * "This patient is not next in the queue — why see them first?"
 *
 * Shown when the server refuses a consultation start with 409 `out_of_order`
 * (Queue Prioritization's call-order check, backend/utils/consultationState.js)
 * — or, in the Visit Workflow, by its pre-flight before that refusal. The
 * reason goes back as `out_of_order_reason` and is kept in the audit log.
 * Used by both the Queue screen and the Visit Workflow's Queue stage.
 */

const QUICK_REASONS = [
  'Patient called earlier did not respond',
  'Patient ahead is waiting for a test result',
  'Doctor asked to see this patient first',
  'Patient ahead stepped out',
  'Quick review / report collection',
];

export interface ExpectedNext {
  patient_name?: string | null;
  display_token?: string | null;
}

export function OutOfOrderDialog({ open, patientName, expected, position, pending = false, onConfirm, onCancel }: {
  open: boolean;
  /** The patient staff are trying to start. */
  patientName: string;
  /** The patient the queue says is next. */
  expected: ExpectedNext | null;
  /** The chosen patient's place among those waiting (1-based), when known. */
  position?: number | null;
  pending?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  const ok = reason.trim().length >= 3;
  const nextName = expected?.patient_name || 'Another patient';
  const nextToken = expected?.display_token ? ` (token ${expected.display_token})` : '';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>See {patientName} out of turn?</DialogTitle>
          <DialogDescription>
            <b>{nextName}{nextToken}</b> is next in the queue
            {position ? <>, and {patientName} is number {position}</> : null}. You can still see {patientName} first —
            give a reason. It is saved in the audit log.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {QUICK_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setReason(r)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-[11.5px] transition-colors',
                reason === r ? 'border-teal bg-accent text-teal' : 'border-border text-ink-muted hover:border-teal hover:text-teal'
              )}
            >
              {r}
            </button>
          ))}
        </div>
        <FormField label="Reason" required>
          <Textarea rows={2} maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Why this patient before the one who is next" />
        </FormField>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>Cancel — keep the queue order</Button>
          <Button disabled={!ok} loading={pending} onClick={() => onConfirm(reason.trim())}>See {patientName} now</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
